import type { AgeClass, AnimalSex } from "@/api/cases/CasesApi";
import { CareProtocolApi } from "@/api/careProtocol/CareProtocolApi";
import { useCreateAnimalCase, useIntakeReasons } from "@/api/cases/useCases";
import { CareSitesApi } from "@/api/careSites/CareSitesApi";
import { usePersistFinderForCases } from "@/api/contacts/useContacts";
import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import { useOrganizationSpecies } from "@/api/species/useOrganizationSpecies";
import { useAuth } from "@/auth/AuthContext";
import { useNetworkStatus } from "@/network/NetworkContext";
import { queryKeys } from "@/api/queryKeys";
import { WIDE_VIEWPORT_BREAKPOINT } from "@/constants/layout";
import { LOADING_LABEL } from "@/constants/uiLabels";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { FloatingLabelDatePicker } from "@/ui/components/FloatingLabelDatePicker";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { FloatingLabelListPicker } from "@/ui/components/FloatingLabelListPicker";
import {
  formatPlaceLine,
  GeoPlaceAutocompleteField,
} from "@/ui/components/GeoPlaceAutocompleteField";
import { EmptyState } from "@/ui/components/EmptyState";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { finderCaseHref } from "@/ui/finder/finderCaseNotice";
import { FinderIntakeCard } from "@/ui/finder/FinderIntakeCard";
import { FinderOfflineChoice } from "@/ui/finder/FinderOfflineChoice";
import { FinderPrivacySaveChoice } from "@/ui/finder/FinderPrivacySaveChoice";
import {
  useClearFinderDraftAfterSavedCase,
  useFinderMode,
} from "@/ui/finder/FinderModeContext";
import {
  emptyFinderDraft,
  FINDER_INTAKE_FAILED,
  FINDER_NAME_SAVE_HINT,
  FINDER_OFFLINE_FINDER,
  FINDER_SAVED_FINDER_FAILED,
  FINDER_STILL_ACTIVE,
  finderDraftHasPayload,
  finderModeAllowsFinalize,
  intakeQueuedLocally,
  finderPayloadWithoutName,
  finderSaveBlockedWithoutPrivacy,
  finderSaveBlockedWithoutProvision,
  isFinderDraftReady,
} from "@/ui/finder/finderMode";
import {
  invalidateIntakeWeightQueries,
  INTAKE_WEIGHT_LOCKED_HINT,
  INTAKE_WEIGHT_RETRY_FALLBACK,
  INTAKE_WEIGHT_RIGHTS_MESSAGE,
  intakeWeightErrorText,
  intakeWeightEvent,
  parseIntakeWeight,
  settleIntakeWeights,
  type IntakeWeightOutcome,
  type IntakeWeightSlot,
} from "@/ui/intake/intakeWeight";
import {
  foundPlaceForSave,
  foundPlaceNeedsHint,
  FUNDORT_UNSELECTED_HINT,
} from "@/utils/foundPlaceDraft";
import {
  assessFoundOn,
  intakeErrorMessage,
  todayIsoLocal,
  uiErrorMessage,
} from "@/utils/isoDate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { viewWhileLoading } from "@/ui/loadingState";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IntakeReasonChips } from "@/ui/intake/IntakeReasonChips";
import { IntakeSpeciesField } from "@/ui/intake/IntakeSpeciesField";
import {
  FUND_DATE_CHECK_HINT,
  INTAKE_KEPT_LINE,
  INTAKE_PLACE_HINT,
  INTAKE_SITE_MIN_WIDTH,
  INTAKE_WEIGHT_FIELD_WIDTH,
  INTAKE_WEIGHT_HINT,
  INTAKE_WEIGHT_LABEL,
  INTAKE_WEIGHT_SUFFIX,
  SPECIES_FREE_TEXT_MAX,
  SPECIES_FREE_TEXT_TOO_LONG,
  SPECIES_CHECK_HINT,
} from "@/ui/intake/intakeCopy";
import {
  intakeFocusAfter,
  intakeStatusLiveProps,
  shouldAutofocusIntakeWeight,
} from "@/ui/intake/intakeFocus";
import { intakeSpeciesForSave, intakeSpeciesMissing } from "@/ui/intake/intakeSpecies";
import { prefillCareSiteId } from "@/ui/intake/intakeCareSite";
import {
  armIntakeFollowup,
  clearIntakeFollowupNotice,
  consumeIntakeWeightFocus,
  rememberIntakeCases,
  requestIntakeWeightFocus,
} from "@/ui/intake/lastCareSite";
import { useCoarseTarget } from "@/ui/intake/useCoarseTarget";
import { useIntakeFollowupNotice } from "@/ui/intake/useIntakeFollowupNotice";
import { useIntakeCareSites } from "@/ui/intake/useIntakeCareSites";
import { Platform, ScrollView, Text, TextInput, useWindowDimensions, View } from "react-native";

const SEX_OPTIONS = [
  { value: "unknown", label: "Unbekannt" },
  { value: "male", label: "Männlich" },
  { value: "female", label: "Weiblich" },
];

const AGE_OPTIONS = [
  { value: "unclear", label: "Unklar" },
  { value: "baby", label: "Baby" },
  { value: "juvenile", label: "Jungtier" },
  { value: "adult", label: "Adult" },
];

function newClientId(): string {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function AnimalNewScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_VIEWPORT_BREAKPOINT;
  const coarse = useCoarseTarget();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const carriedNotice = useIntakeFollowupNotice(userId);
  const { organizations, loading: orgsLoading } = useMyOrganizations();
  const orgId = organizations[0]?.id;
  const {
    sites: orgSites,
    loading: sitesLoading,
    lastUsedId,
    accessFailed,
    remember,
  } = useIntakeCareSites(orgId);
  const { species: speciesOptions, listError: speciesListError } = useOrganizationSpecies(orgId);
  const { reasons } = useIntakeReasons(orgId);
  const createCase = useCreateAnimalCase();
  const persistFinder = usePersistFinderForCases();
  const queryClient = useQueryClient();
  const finderMode = useFinderMode();
  const { isOnline } = useNetworkStatus();
  const [savedCaseIds, setSavedCaseIds] = useState<string[] | null>(null);
  const savedCaseIdsRef = useRef<string[] | null>(null);
  const weightSlotRef = useRef<IntakeWeightSlot | null>(null);
  const [weightLocked, setWeightLocked] = useState(false);
  const [weightSaving, setWeightSaving] = useState(false);
  const weightRightsTold = useRef(false);
  const intakeWasQueued = useRef(false);
  const [dropConfirm, setDropConfirm] = useState(false);
  const [privacyChoice, setPrivacyChoice] = useState(false);
  const [finderHeldLocal, setFinderHeldLocal] = useState(false);
  const offlineFinderBlocked = !isOnline && finderDraftHasPayload(finderMode.draft);
  const finderChoiceBlocked = offlineFinderBlocked || finderHeldLocal;
  useClearFinderDraftAfterSavedCase(savedCaseIds, finderMode.clearDraft);
  const scrollRef = useRef<ScrollView>(null);
  const nameRef = useRef<TextInput>(null);
  const weightRef = useRef<TextInput>(null);
  const fundortRef = useRef<TextInput>(null);
  const extraRef = useRef<TextInput>(null);
  const reasonRef = useRef<TextInput>(null);
  const finderNameRef = useRef<TextInput>(null);
  const notesRef = useRef<TextInput>(null);
  const saveButtonRef = useRef<View>(null);
  const submitLock = useRef(false);

  const [clientCreateId, setClientCreateId] = useState(newClientId);
  const [name, setName] = useState("");
  const [intakeWeight, setIntakeWeight] = useState("");
  const [sex, setSex] = useState<AnimalSex>("unknown");
  const [ageClass, setAgeClass] = useState<AgeClass>("unclear");
  const [careSiteId, setCareSiteId] = useState<string | null>(null);
  const [speciesId, setSpeciesId] = useState<string | null>(null);
  const [speciesFreeText, setSpeciesFreeText] = useState("");
  const [speciesRequired, setSpeciesRequired] = useState(false);
  const [speciesFocus, setSpeciesFocus] = useState(0);
  const speciesIdRef = useRef<string | null>(null);
  const speciesFreeTextRef = useRef("");
  const onSpeciesId = (id: string | null) => {
    speciesIdRef.current = id;
    setSpeciesId(id);
    if (!intakeSpeciesMissing({ organizationSpeciesId: id, speciesFreeText: speciesFreeTextRef.current })) {
      setSpeciesRequired(false);
      setSpeciesFocus(0);
      setMessage((current) => (current === SPECIES_CHECK_HINT ? null : current));
    }
  };
  const onSpeciesFreeText = (text: string) => {
    speciesFreeTextRef.current = text;
    setSpeciesFreeText(text);
    if (!intakeSpeciesMissing({ organizationSpeciesId: speciesIdRef.current, speciesFreeText: text })) {
      setSpeciesRequired(false);
      setSpeciesFocus(0);
      setMessage((current) => (current === SPECIES_CHECK_HINT ? null : current));
    }
  };
  const [receipt, setReceipt] = useState<{
    caseId: string;
    caseNumber: string | null;
    queued: boolean;
    arrivalNotice: string | null;
  } | null>(null);
  const siteTouched = useRef(false);
  const [siteFocus, setSiteFocus] = useState(0);
  const [sexFocus, setSexFocus] = useState(0);
  const [dateFocus, setDateFocus] = useState(0);
  const [focusToken, setFocusToken] = useState(0);
  const caseNumberRef = useRef<string | null>(null);
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [foundOn, setFoundOn] = useState<string | null>(() => todayIsoLocal());
  const [foundPlaceId, setFoundPlaceId] = useState<string | null>(null);
  const [foundPlaceText, setFoundPlaceText] = useState("");
  const [foundPlaceExtra, setFoundPlaceExtra] = useState("");
  const [selectedReasons, setSelectedReasons] = useState<Set<string>>(new Set());
  const [reasonNote, setReasonNote] = useState("");
  const [notes, setNotes] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<"muted" | "danger">("muted");
  const [foundWarning, setFoundWarning] = useState<string | null>(null);
  const [foundOnFieldValid, setFoundOnFieldValid] = useState(true);
  const onFoundOnValidityChange = useCallback((valid: boolean) => {
    setFoundOnFieldValid(valid);
  }, []);

  useEffect(() => {
    const a = assessFoundOn(foundOn);
    setFoundWarning(a.warning ?? null);
  }, [foundOn]);

  const siteOptions = useMemo(
    () => orgSites.map((s) => ({ value: s.id, label: s.name })),
    [orgSites],
  );

  useEffect(() => {
    if (sitesLoading || siteTouched.current) return;
    const next = prefillCareSiteId(orgSites, lastUsedId);
    setCareSiteId((current) => (current === next ? current : next));
  }, [orgSites, lastUsedId, sitesLoading]);

  useEffect(() => {
    if (receipt || !shouldAutofocusIntakeWeight(coarse)) return;
    if (!consumeIntakeWeightFocus()) return;
    const id = requestAnimationFrame(() => weightRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [receipt, coarse, focusToken]);

  const nodesQ = useQuery({
    queryKey: queryKeys.careSites.nodes(careSiteId ?? ""),
    queryFn: () => CareSitesApi.listPlaceNodes(careSiteId!),
    enabled: !!careSiteId,
  });

  const placeOptions = useMemo(() => {
    const nodes = nodesQ.data ?? [];
    return [
      { value: null as string | null, label: "Noch nicht zugeordnet" },
      ...nodes
        .filter((n) => n.is_occupiable)
        .map((n) => ({ value: n.id, label: n.name })),
    ];
  }, [nodesQ.data]);

  const colGap = theme.space.md;
  const half = wide
    ? ({ flex: 1, minWidth: 0 } as const)
    : ({ width: "100%" } as const);

  const toggleReason = (id: string) => {
    setSelectedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSubmit = async (dropFinder = false) => {
    if (
      submitLock.current ||
      createCase.isPending ||
      weightSaving ||
      persistFinder.isPending
    ) {
      return;
    }
    // Verwerfen schließt den Dialog sofort, auch wenn Art oder Datum danach fehlen.
    if (dropFinder) {
      setDropConfirm(false);
      setPrivacyChoice(false);
    }
    // Art zuerst, dann die Finder-Hinweise. Danach Pflegestelle, Gewicht, Datum.
    // Ohne Verein ist das Art-Feld nicht montiert.
    if (!orgId) {
      setMessageTone("muted");
      setMessage("Keine aktive Organisation.");
      return;
    }
    if (speciesFreeTextRef.current.trim().length > SPECIES_FREE_TEXT_MAX) {
      setMessageTone("danger");
      setMessage(SPECIES_FREE_TEXT_TOO_LONG);
      return;
    }
    if (
      intakeSpeciesMissing({
        organizationSpeciesId: speciesIdRef.current,
        speciesFreeText: speciesFreeTextRef.current,
      })
    ) {
      setMessageTone("muted");
      setMessage(SPECIES_CHECK_HINT);
      setSpeciesRequired(true);
      setSpeciesFocus((n) => n + 1);
      return;
    }
    setMessage(null);
    setMessageTone("muted");
    const draft = dropFinder ? emptyFinderDraft() : finderMode.draft;
    if (!dropFinder && finderPayloadWithoutName(draft)) {
      setMessage(FINDER_NAME_SAVE_HINT);
      finderMode.requestScrollToFinder();
      return;
    }
    if (!dropFinder && !isOnline && finderDraftHasPayload(draft)) {
      setMessage(FINDER_OFFLINE_FINDER);
      return;
    }
    if (!dropFinder && finderHeldLocal) {
      setMessage(FINDER_OFFLINE_FINDER);
      return;
    }
    if (!careSiteId) {
      setMessage("Bitte eine Pflegestelle wählen.");
      return;
    }
    const parsedWeight = parseIntakeWeight(intakeWeight);
    const weightError = weightSlotRef.current
      ? null
      : intakeWeightErrorText(intakeWeight);
    if (weightError) {
      setMessageTone("danger");
      setMessage(uiErrorMessage(new Error(weightError), weightError));
      return;
    }
    if (!foundOnFieldValid) {
      setMessage("Das Funddatum ist ungültig.");
      return;
    }
    const dateAssessed = assessFoundOn(foundOn);
    if (!dateAssessed.ok) {
      setMessage(dateAssessed.error ?? "Ungültiges Funddatum.");
      return;
    }
    if (!finderModeAllowsFinalize(finderMode.active)) {
      setMessage(FINDER_STILL_ACTIVE);
      return;
    }
    submitLock.current = true;
    try {
      if (!dropFinder && finderSaveBlockedWithoutPrivacy(draft)) {
        setPrivacyChoice(true);
        return;
      }
      if (!dropFinder && finderSaveBlockedWithoutProvision(draft)) {
        setPrivacyChoice(false);
        finderMode.noteProvisionAttempt();
        finderMode.requestScrollToFinder();
        return;
      }
      setPrivacyChoice(false);
      let caseIds = savedCaseIdsRef.current;
      if (!caseIds) {
        const place = foundPlaceForSave({
          placeId: foundPlaceId,
          typed: foundPlaceText,
          extra: foundPlaceExtra,
        });
        const species = intakeSpeciesForSave({
          organizationSpeciesId: speciesIdRef.current,
          speciesFreeText: speciesFreeTextRef.current,
        });
        const created = await createCase.mutateAsync({
          organizationId: orgId,
          clientCreateId,
          careSiteId,
          placeId,
          name: name.trim() || null,
          sex,
          ageClass,
          foundOn: dateAssessed.date,
          foundPlaceId: place.foundPlaceId,
          foundPlaceText: place.foundPlaceText,
          intakeReasonIds: [...selectedReasons],
          intakeReasonNote: reasonNote.trim() || null,
          generalNotes: notes.trim() || null,
          organizationSpeciesId: species.organizationSpeciesId,
          speciesFreeText: species.speciesFreeText,
        });
        caseIds = [created.id];
        caseNumberRef.current = created.case_number ?? null;
        savedCaseIdsRef.current = caseIds;
        setSavedCaseIds(caseIds);
        if (intakeQueuedLocally(created)) intakeWasQueued.current = true;
      }
      const grams =
        weightSlotRef.current?.grams ??
        (parsedWeight.ok ? parsedWeight.grams : null);
      if (grams != null) {
        const slot: IntakeWeightSlot = weightSlotRef.current ?? {
          caseId: caseIds[0],
          clientEntryId: newClientId(),
          recordedAt: new Date().toISOString(),
          grams,
          saved: false,
          abandoned: false,
        };
        slot.caseId = caseIds[0];
        weightSlotRef.current = slot;
        setWeightLocked(true);
        setWeightSaving(true);
        let outcome: IntakeWeightOutcome;
        try {
          outcome = await settleIntakeWeights(
            [slot],
            async (current) => {
              await CareProtocolApi.submitEntry({
                caseId: current.caseId,
                clientEntryId: current.clientEntryId,
                recordedAt: current.recordedAt,
                events: [intakeWeightEvent(current.grams)],
              });
              invalidateIntakeWeightQueries(queryClient, current.caseId);
            }
          );
        } finally {
          setWeightSaving(false);
        }
        if (outcome.kind === "retry") {
          setMessageTone("danger");
          setMessage(uiErrorMessage(outcome.error, INTAKE_WEIGHT_RETRY_FALLBACK));
          return;
        }
        if (outcome.kind === "rights" && !weightRightsTold.current) {
          weightRightsTold.current = true;
          setMessageTone("danger");
          setMessage(
            uiErrorMessage(
              new Error(INTAKE_WEIGHT_RIGHTS_MESSAGE),
              INTAKE_WEIGHT_RIGHTS_MESSAGE
            )
          );
          return;
        }
      }
      if (
        !dropFinder &&
        intakeWasQueued.current &&
        finderDraftHasPayload(draft)
      ) {
        setFinderHeldLocal(true);
        return;
      }
      let arrivalNotice: string | null = null;
      if (!dropFinder && isFinderDraftReady(draft)) {
        try {
          const saved = await persistFinder.mutateAsync({
            organizationId: orgId,
            caseIds,
            contact: {
              organizationId: orgId,
              displayName: draft.displayName.trim(),
              notes: draft.notes.trim() || null,
              phone: draft.phone.trim() || null,
              email: draft.email.trim() || null,
              address: draft.address.trim() || null,
              preferredChannel: draft.preferredChannel.trim() || null,
              privacyNoticeVersion: draft.privacyNoticeVersion,
              privacyProvisionKind: draft.privacyProvisionKind,
              privacyDocumented: draft.privacyDocumented,
              clientContactId: draft.clientContactId,
              matchedContactId: draft.matchedContactId,
              reuseBaseline: draft.matchedBaseline,
            },
          });
          arrivalNotice = saved?.notice ?? null;
        } catch {
          setMessageTone("danger");
          setMessage(FINDER_SAVED_FINDER_FAILED);
          finderMode.requestScrollToFinder();
          return;
        }
      }
      setFinderHeldLocal(false);
      savedCaseIdsRef.current = null;
      weightSlotRef.current = null;
      setWeightLocked(false);
      setSavedCaseIds(null);
      clearIntakeFollowupNotice();
      rememberIntakeCases(userId, caseIds);
      remember(careSiteId);
      setReceipt({
        caseId: caseIds[0],
        caseNumber: caseNumberRef.current,
        queued: intakeWasQueued.current,
        arrivalNotice,
      });
    } catch (error: unknown) {
      setMessageTone("danger");
      setMessage(intakeErrorMessage(error, FINDER_INTAKE_FAILED));
    } finally {
      submitLock.current = false;
    }
  };

  const focusSave = () => {
    const node = saveButtonRef.current as { focus?: () => void } | null;
    node?.focus?.();
  };

  const focusFrom = (field: "species" | "weight" | "name" | "foundOn" | "notes") => {
    const target = intakeFocusAfter(field, !!careSiteId);
    if (target === "careSite") setSiteFocus((n) => n + 1);
    else if (target === "weight") weightRef.current?.focus();
    else if (target === "name") nameRef.current?.focus();
    else if (target === "sex") setSexFocus((n) => n + 1);
    else if (target === "foundOn") setDateFocus((n) => n + 1);
    else if (target === "foundPlace") fundortRef.current?.focus();
    else focusSave();
  };

  const takeAnother = () => {
    armIntakeFollowup({
      userId,
      caseIds: receipt ? [receipt.caseId] : [],
      text: receipt?.arrivalNotice ?? null,
    });
    if (shouldAutofocusIntakeWeight(coarse)) requestIntakeWeightFocus();
    finderMode.clearDraft();
    // Folgetier meist dieselbe Art: Chip und Freitext bleiben und sind
    // nach dem Zurücksetzen wieder sichtbar. Name, Gewicht und Finder werden geleert.
    setSpeciesRequired(false);
    setSpeciesFocus(0);
    setReceipt(null);
    setFocusToken((n) => n + 1);
    setClientCreateId(newClientId());
    setName("");
    setIntakeWeight("");
    setSex("unknown");
    setAgeClass("unclear");
    setPlaceId(null);
    setFoundPlaceId(null);
    setFoundPlaceText("");
    setFoundPlaceExtra("");
    setSelectedReasons(new Set());
    setReasonNote("");
    setNotes("");
    setMessage(null);
    setWeightLocked(false);
    weightSlotRef.current = null;
    caseNumberRef.current = null;
    intakeWasQueued.current = false;
    weightRightsTold.current = false;
  };

  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;
  const saveDisabled =
    finderChoiceBlocked ||
    privacyChoice ||
    createCase.isPending ||
    weightSaving ||
    persistFinder.isPending ||
    !orgId ||
    !foundOnFieldValid ||
    !finderModeAllowsFinalize(finderMode.active);
  const saveDisabledRef = useRef(saveDisabled);
  saveDisabledRef.current = saveDisabled;
  useEffect(() => {
    if (
      Platform.OS !== "web" ||
      receipt ||
      typeof window === "undefined" ||
      typeof window.addEventListener !== "function"
    ) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat) return;
      if (!(event.ctrlKey || event.metaKey) || event.key !== "Enter") return;
      if (saveDisabledRef.current) return;
      event.preventDefault();
      void submitRef.current(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [receipt]);

  const touchTarget = coarse ? { minHeight: theme.control.heightTouch } : undefined;
  const actionBar = receipt ? (
    <View style={{ flexDirection: wide ? "row" : "column", gap: theme.space.sm }}>
      <AppButton onPress={takeAnother} style={touchTarget}>
        Weiteres Tier aufnehmen
      </AppButton>
      <AppButton
        variant="outline"
        style={touchTarget}
        onPress={() => {
          finderMode.clearDraft();
          router.replace(
            finderCaseHref(receipt.caseId, receipt.arrivalNotice) as never,
          );
        }}
      >
        Akte öffnen
      </AppButton>
    </View>
  ) : (
    <View
      style={{
        flexDirection: wide ? "row" : "column",
        justifyContent: wide ? "flex-end" : "flex-start",
        alignItems: wide ? "center" : "stretch",
        gap: theme.space.sm,
      }}
    >
      {message ? (
        <Text
          {...intakeStatusLiveProps(message, messageTone)}
          style={{
            color: messageTone === "danger" ? theme.color.dangerText : theme.color.muted,
            flex: wide ? 1 : undefined,
          }}
        >
          {message}
        </Text>
      ) : (
        wide && <View style={{ flex: 1 }} />
      )}
      <FinderPrivacySaveChoice
        visible={privacyChoice}
        caseAlreadySaved={savedCaseIds != null}
        onHandBack={() => {
          setPrivacyChoice(false);
          void finderMode.start();
        }}
        onDrop={() => {
          void onSubmit(true);
        }}
      />
      <FinderOfflineChoice
        blocked={finderChoiceBlocked}
        confirming={dropConfirm}
        onRequest={() => setDropConfirm(true)}
        onCancel={() => setDropConfirm(false)}
        onConfirm={() => {
          void onSubmit(true);
        }}
      />
      {!foundOnFieldValid ? (
        <Text
          accessibilityLiveRegion="polite"
          style={{ color: theme.color.dangerText, fontSize: theme.fontSize.small }}
        >
          {FUND_DATE_CHECK_HINT}
        </Text>
      ) : null}
      <AppButton
        ref={saveButtonRef}
        accessibilityRole="button"
        onPress={() => {
          void onSubmit(false);
        }}
        disabled={saveDisabled}
        style={{
          ...(coarse ? { minHeight: theme.control.heightTouch } : {}),
          ...(wide ? { alignSelf: "flex-end" as const, minWidth: 180 } : {}),
        }}
      >
        {createCase.isPending || weightSaving ? "Speichern…" : "Aufnehmen"}
      </AppButton>
    </View>
  );

  return (
    <Screen
      title="Tier aufnehmen"
      scroll
      scrollRef={scrollRef}
      maxWidth={theme.layout.formMax}
      footer={actionBar}
      contentStyle={{ paddingBottom: theme.space.md }}
    >
      <View style={{ gap: theme.space.md }}>
        {receipt ? (
          <View style={{ gap: theme.space.sm }}>
            <Text
              accessibilityLiveRegion="polite"
              style={{ color: theme.color.text, fontSize: theme.fontSize.body }}
            >
              {receipt.queued
                ? "Lokal gespeichert. Die Nummer folgt nach der Synchronisation."
                : receipt.caseNumber
                  ? `Aufgenommen: ${receipt.caseNumber}`
                  : "Aufgenommen."}
            </Text>
            <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
              {INTAKE_KEPT_LINE}
            </Text>
            {receipt.arrivalNotice ? (
              <Text
                accessibilityLiveRegion="polite"
                style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
              >
                {receipt.arrivalNotice}
              </Text>
            ) : null}
          </View>
        ) : null}
        {!receipt && carriedNotice ? (
          <Text
            accessibilityLiveRegion="polite"
            style={{ color: theme.color.warningText, fontSize: theme.fontSize.body }}
          >
            {carriedNotice}
          </Text>
        ) : null}

        {!receipt ? (
        <>
        <SectionCard title="Unterbringung" bodyStyle={{ paddingVertical: theme.space.sm }}>
          {viewWhileLoading(orgsLoading, !orgId) === "loading" ||
          (orgId && sitesLoading) ? (
            <Text style={{ color: theme.color.muted }}>{LOADING_LABEL}</Text>
          ) : !orgId ? (
            <EmptyState
              title="Keine aktive Organisation."
              hint="Ohne Verein ist eine Aufnahme nicht möglich."
            />
          ) : accessFailed ? (
            <EmptyState
              title="Pflegestellen nicht geladen."
              hint="Die Stellen mit Schreibrecht konnten nicht gelesen werden."
            />
          ) : siteOptions.length === 0 ? (
            <EmptyState
              title="Keine Pflegestelle mit Schreibrecht."
              hint="Eine Aufnahme ist nur in einer Stelle möglich, für die du Schreibrecht hast."
            />
          ) : (
            <View style={{ gap: theme.space.sm }}>
              <IntakeSpeciesField
                options={speciesOptions}
                speciesId={speciesId}
                freeText={speciesFreeText}
                onChangeSpeciesId={onSpeciesId}
                onChangeFreeText={onSpeciesFreeText}
                onFreeTextSubmit={() => focusFrom("species")}
                listError={speciesListError}
                requiredError={speciesRequired}
                focusRequest={speciesFocus}
                scrollRef={scrollRef}
              />
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: colGap,
                  alignItems: "flex-start",
                }}
              >
                <View style={{ flex: 1, minWidth: INTAKE_SITE_MIN_WIDTH }}>
                  <FloatingLabelListPicker
                    label="Pflegestelle"
                    options={siteOptions}
                    value={careSiteId}
                    focusRequest={siteFocus}
                    onChange={(v) => {
                      siteTouched.current = true;
                      setCareSiteId(v);
                      setPlaceId(null);
                    }}
                    required
                    placeholder="Pflegestelle wählen…"
                  />
                </View>
                <FloatingLabelInput
                  ref={weightRef}
                  label={INTAKE_WEIGHT_LABEL}
                  value={intakeWeight}
                  onChangeText={setIntakeWeight}
                  editable={!weightLocked}
                  width="weight"
                  containerStyle={{ width: INTAKE_WEIGHT_FIELD_WIDTH }}
                  rightElement={
                    <Text style={{ color: theme.color.muted, fontWeight: "700" }}>
                      {INTAKE_WEIGHT_SUFFIX}
                    </Text>
                  }
                  hint={weightLocked ? undefined : INTAKE_WEIGHT_HINT}
                  keyboardType="decimal-pad"
                  returnKeyType="next"
                  blurOnSubmit={false}
                  onSubmitEditing={() => focusFrom("weight")}
                />
              </View>
              {weightLocked ? (
                <Text
                  accessibilityLiveRegion="polite"
                  style={{
                    color: theme.color.warningText,
                    fontSize: theme.fontSize.small,
                  }}
                >
                  {INTAKE_WEIGHT_LOCKED_HINT}
                </Text>
              ) : null}
              <FloatingLabelListPicker
                label="Unterbringungsplatz"
                options={placeOptions}
                value={placeId}
                onChange={setPlaceId}
                placeholder="Optional"
                hint={INTAKE_PLACE_HINT}
              />
            </View>
          )}
        </SectionCard>

        <SectionCard title="Basisdaten" bodyStyle={{ paddingVertical: theme.space.sm }}>
          <View style={{ gap: theme.space.sm }}>
            <FloatingLabelInput
              ref={nameRef}
              label="Name"
              value={name}
              onChangeText={setName}
              hint="Optional. Kurzname für die Akte."
              returnKeyType="next"
              blurOnSubmit={false}
              onSubmitEditing={() => focusFrom("name")}
            />
            <View
              style={{
                flexDirection: wide ? "row" : "column",
                gap: colGap,
              }}
            >
              <View style={half}>
                <FloatingLabelListPicker
                  label="Geschlecht"
                  options={SEX_OPTIONS}
                  value={sex}
                  focusRequest={sexFocus}
                  onChange={(v) => setSex((v as AnimalSex) ?? "unknown")}
                />
              </View>
              <View style={half}>
                <FloatingLabelListPicker
                  label="Altersklasse"
                  options={AGE_OPTIONS}
                  value={ageClass}
                  onChange={(v) => setAgeClass((v as AgeClass) ?? "unclear")}
                />
              </View>
            </View>
          </View>
        </SectionCard>

        <SectionCard title="Fund und Aufnahme" bodyStyle={{ paddingVertical: theme.space.sm }}>
          <View style={{ gap: theme.space.sm }}>
            <View
              style={{
                flexDirection: wide ? "row" : "column",
                gap: colGap,
              }}
            >
              <View style={half}>
                <FloatingLabelDatePicker
                  label="Funddatum"
                  value={foundOn}
                  onChange={setFoundOn}
                  onValidityChange={onFoundOnValidityChange}
                  optional
                  allowClear
                  maxIso={todayIsoLocal()}
                  placeholder="Heute – oder entfernen"
                  focusRequest={dateFocus}
                  onSubmitEditing={() => focusFrom("foundOn")}
                />
              </View>
              <View style={half}>
                <GeoPlaceAutocompleteField
                  label="Fundort"
                  inputRef={fundortRef}
                  valuePlaceId={foundPlaceId}
                  valueText={foundPlaceText}
                  onChangeText={(t) => {
                    setFoundPlaceText(t);
                    setFoundPlaceId(null);
                  }}
                  onSelect={(place) => {
                    setFoundPlaceId(place.id);
                    setFoundPlaceText(formatPlaceLine(place));
                  }}
                  placeholder="Fundort oder PLZ suchen"
                  hint="Ort oder Postleitzahl wählen. Eine Straße ist nicht nötig."
                  warning={
                    foundPlaceNeedsHint(foundPlaceId, foundPlaceText)
                      ? FUNDORT_UNSELECTED_HINT
                      : null
                  }
                  returnKeyType="next"
                  blurOnSubmit={false}
                  onSubmitEditing={() => extraRef.current?.focus()}
                />
              </View>
            </View>
            {foundWarning ? (
              <Text style={{ color: theme.color.warningText, fontSize: 12 }}>
                {foundWarning}
              </Text>
            ) : null}
            <FloatingLabelInput
              ref={extraRef}
              label="Ergänzung zum Fundort"
              value={foundPlaceExtra}
              onChangeText={setFoundPlaceExtra}
              returnKeyType="next"
              blurOnSubmit={false}
              onSubmitEditing={() => reasonRef.current?.focus()}
            />
            <Text
              style={{
                color: theme.color.textSecondary,
                fontSize: 13,
                fontWeight: "600",
              }}
            >
              Aufnahmegründe
            </Text>
            <IntakeReasonChips
              reasons={reasons}
              selected={selectedReasons}
              onToggle={toggleReason}
            />
            <FloatingLabelInput
              ref={reasonRef}
              label="Ergänzung zum Aufnahmegrund"
              value={reasonNote}
              onChangeText={setReasonNote}
              returnKeyType="next"
              blurOnSubmit={false}
              onSubmitEditing={() => {
                if (finderNameRef.current) finderNameRef.current.focus();
                else notesRef.current?.focus();
              }}
            />
          </View>
        </SectionCard>

        <FinderIntakeCard
          organizationId={orgId}
          scrollRef={scrollRef}
          nameInputRef={finderNameRef}
          onNotesSubmitEditing={() => notesRef.current?.focus()}
          intro="Die Finder-Person trägt die Kontaktdaten im eigenen Bildschirm ein. Nach der Rückgabe prüfst du sie hier."
        />

        <SectionCard title="Notizen" bodyStyle={{ paddingVertical: theme.space.sm }}>
          <FloatingLabelInput
            ref={notesRef}
            label="Allgemeine Notizen"
            value={notes}
            onChangeText={setNotes}
            hint="Optional. Eine Zeile."
            returnKeyType="next"
            blurOnSubmit={false}
            onSubmitEditing={() => focusFrom("notes")}
          />
        </SectionCard>
        </>
        ) : null}
      </View>
    </Screen>
  );
}
